import {
  default as React,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import {
  useApi,
  configApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useCurrentUserEmail } from './hooks';
import { isUserAuthorizedForEntity, useSkyStageAdminEmails } from './hooks';
import { useMicroappEnvironmentStatus } from './hooks/useMicroappEnvironmentStatus';

interface SecretDetail {
  key: string;
  lastUpdated?: string; // ISO timestamp from GCP SM label
  lastUpdatedBy?: string; // user email from GCP SM label
}

/** Unified dual-env secret from /secrets/:appName/dual-env */
interface DualEnvSecret {
  key: string;
  stagingExists: boolean;
  productionExists: boolean;
  stagingLastUpdated: string | null;
  productionLastUpdated: string | null;
  stagingUpdatedBy: string | null;
  productionUpdatedBy: string | null;
}

interface AuditEntry {
  action: 'created' | 'updated' | 'deleted';
  key: string;
  userEmail: string;
  timestamp: string;
  appName: string;
  environment?: 'staging' | 'production'; // IDP-983: optional for backward compat
}

/**
 * Normalize audit entry for display:
 * - Strip STAGING_ prefix from legacy keys and infer environment
 * - Convert null environment to appropriate default based on key prefix
 */
function normalizeAuditEntry(entry: AuditEntry): AuditEntry {
  // If environment is already set, just return (new records)
  if (entry.environment) {
    return entry;
  }
  // Legacy records: infer environment from key prefix
  if (entry.key.startsWith('STAGING_')) {
    return {
      ...entry,
      key: entry.key.replace(/^STAGING_/, ''),
      environment: 'staging',
    };
  }
  // No prefix and no environment = production (default)
  return {
    ...entry,
    environment: 'production',
  };
}

/** Format an ISO timestamp to a human-readable relative string. */
function formatRelative(iso: string | undefined): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * SecretsTab — read-only entity page tab showing GCP Secret Manager secrets.
 *
 * Only shows secret key names and metadata (last updated, updated by).
 * Secret values are NEVER displayed.
 *
 * To add, edit, or delete secrets, users must use the Deploy/Redeploy form.
 * The tab is only visible to app owners (gated in EntityPage.tsx via `if` prop).
 */
export const SecretsTab = () => {
  const { entity } = useEntity();
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const currentUserEmail = useCurrentUserEmail();
  const adminEmails = useSkyStageAdminEmails();

  const appName = entity.metadata.name;
  const isOwner = isUserAuthorizedForEntity(
    entity,
    currentUserEmail,
    adminEmails,
  );

  // ─── BYOG annotations (may be stale until catalog refreshes) ─────────────
  const entityAnnotations = entity.metadata.annotations ?? {};
  const hasByogSecrets =
    entityAnnotations['microapp/hasByogSecrets'] === 'true';
  const byogGcpProjectId = entityAnnotations['microapp/byogGcpProjectId'] || '';
  const byogSecretNames = entityAnnotations['microapp/byogSecretNames']
    ? entityAnnotations['microapp/byogSecretNames'].split(',').filter(Boolean)
    : [];

  // ─── GSM annotation keys (fallback for legacy apps) ──────────────────────
  // Legacy apps have secrets in GCP SM named without the qp-{shortId}- prefix
  // so listSecretDetails returns empty. In that case we fall back to displaying
  // the key names from the catalog annotation so the tab isn't blank.
  const hasGsmSecrets = entityAnnotations['microapp/hasGsmSecrets'] === 'true';
  const annotationGsmKeys =
    hasGsmSecrets && entityAnnotations['microapp/gsmSecretKeys']
      ? entityAnnotations['microapp/gsmSecretKeys']
          .split(',')
          .map(k => k.trim())
          .filter(Boolean)
      : [];

  // ─── Staging environment detection ────────────────────────────────────────
  const stagingEnabled =
    entityAnnotations['microapp/stagingEnabled'] === 'true';

  // IDP-1186: Use live environment status to determine which secrets to show
  const envStatus = useMicroappEnvironmentStatus(appName);
  const stagingDeployed =
    envStatus.status?.staging?.deployed &&
    !envStatus.status?.staging?.scaledToZero;
  const productionDeployed = envStatus.status?.production?.deployed;

  // ─── Existing secrets ─────────────────────────────────────────────────────
  const [secretDetails, setSecretDetails] = useState<SecretDetail[]>([]);
  const [dualEnvSecrets, setDualEnvSecrets] = useState<DualEnvSecret[]>([]);
  const [byogSecretDetails, setByogSecretDetails] = useState<SecretDetail[]>(
    [],
  );
  const [apiByogGcpProjectId, setApiByogGcpProjectId] = useState<string>('');
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // ─── Audit log ────────────────────────────────────────────────────────────
  const [auditEntries, setAuditEntries] = useState<AuditEntry[]>([]);
  const [loadingAudit, setLoadingAudit] = useState(false);

  // ─── Entity refresh ────────────────────────────────────────────────────────
  const [isRefreshing, setIsRefreshing] = useState(false);
  const handleRefreshEntity = async () => {
    setIsRefreshing(true);
    try {
      await catalogApi.refreshEntity(stringifyEntityRef(entity));
      // Reload the page after a brief delay so the catalog can process the refresh
      // and the entity context is re-populated with fresh annotations.
      setTimeout(() => window.location.reload(), 3000);
    } catch {
      setIsRefreshing(false);
    }
  };

  // ─── Helpers ─────────────────────────────────────────────────────────────
  const getAuthHeaders = useCallback(async (): Promise<HeadersInit> => {
    const { token } = await identityApi.getCredentials();
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }, [identityApi]);

  const backendUrl = config.getString('backend.baseUrl');
  const secretsBase = `${backendUrl}/api/microapps-api/secrets/${appName}`;

  // ─── Load existing secrets ───────────────────────────────────────────────
  const loadSecretDetails = useCallback(async () => {
    setLoadingKeys(true);
    setFetchError(null);
    try {
      const headers = await getAuthHeaders();

      // IDP-983: For staging-enabled apps, use the dual-env endpoint to get
      // secrets per environment (staging vs production).
      if (stagingEnabled) {
        const dualRes = await fetch(`${secretsBase}/dual-env`, {
          headers,
          credentials: 'include',
        });
        if (dualRes.ok) {
          const dualData = await dualRes.json();
          setDualEnvSecrets(
            Array.isArray(dualData.secrets) ? dualData.secrets : [],
          );
          // Also set secretDetails for backward compat with existing logic
          setSecretDetails(
            Array.isArray(dualData.secrets)
              ? dualData.secrets.map((s: DualEnvSecret) => ({
                  key: s.key,
                  lastUpdated:
                    s.productionLastUpdated ||
                    s.stagingLastUpdated ||
                    undefined,
                  lastUpdatedBy:
                    s.productionUpdatedBy || s.stagingUpdatedBy || undefined,
                }))
              : [],
          );
          setLoadingKeys(false);
          return;
        }
        // Fall through to legacy endpoint if dual-env fails (e.g., BYOG mode)
      }

      const res = await fetch(secretsBase, { headers, credentials: 'include' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const errorMessage =
          typeof body.error === 'string'
            ? body.error
            : (body.error?.message as string | undefined) ||
              `Failed to load secrets (${res.status})`;
        throw new Error(errorMessage);
      }
      const data = await res.json();
      setSecretDetails(
        Array.isArray(data.secrets)
          ? data.secrets.map((s: string | SecretDetail) =>
              typeof s === 'string'
                ? { key: s } // fallback for legacy shape
                : s,
            )
          : [],
      );
      // Parse BYOG secrets returned by the unified endpoint
      setByogSecretDetails(
        Array.isArray(data.byogSecrets)
          ? data.byogSecrets.map((s: string | SecretDetail) =>
              typeof s === 'string' ? { key: s } : s,
            )
          : [],
      );
      setApiByogGcpProjectId(
        typeof data.byogGcpProjectId === 'string' ? data.byogGcpProjectId : '',
      );
    } catch (err: unknown) {
      setFetchError(
        err instanceof Error ? err.message : 'Failed to load secrets',
      );
    } finally {
      setLoadingKeys(false);
    }
  }, [secretsBase, getAuthHeaders, stagingEnabled]);

  // ─── Load audit log ───────────────────────────────────────────────────────
  const loadAuditLog = useCallback(async () => {
    setLoadingAudit(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${secretsBase}/audit-log`, {
        headers,
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setAuditEntries(data.entries ?? []);
      }
    } finally {
      setLoadingAudit(false);
    }
  }, [secretsBase, getAuthHeaders]);

  useEffect(() => {
    if (isOwner) {
      loadSecretDetails();
      loadAuditLog();
    } else if (currentUserEmail) {
      // Email resolved but user is not an owner — stop the initial loading spinner
      setLoadingKeys(false);
    }
    // When currentUserEmail === '' (still resolving), keep loadingKeys=true
    // so the spinner persists until we know the ownership status.
  }, [isOwner, currentUserEmail, loadSecretDetails, loadAuditLog]);

  // ─── Audit-derived BYOG detection ────────────────────────────────────────
  // When the Backstage catalog entity is stale (not yet re-indexed after the
  // last BYOG switch), hasByogSecrets/byogSecretNames may show the old mode.
  // We cross-reference the live audit log to infer BYOG status even when
  // annotations haven't caught up.
  //
  // Active keys: keys whose most recent audit entry is 'created'/'updated'
  // (i.e. not 'deleted').  Keys that are active but absent from the live GCP SM
  // response (secretDetails) are treated as potential BYOG secrets.
  const auditDerivedByogDetails = useMemo((): SecretDetail[] => {
    if (secretDetails.length > 0) return []; // managed secrets exist — not BYOG
    const latestByKey = new Map<string, AuditEntry>();
    for (const entry of auditEntries) {
      const existing = latestByKey.get(entry.key);
      if (!existing || entry.timestamp > existing.timestamp) {
        latestByKey.set(entry.key, entry);
      }
    }
    const result: SecretDetail[] = [];
    for (const [key, entry] of latestByKey) {
      if (entry.action !== 'deleted') {
        result.push({
          key,
          lastUpdated: entry.timestamp,
          lastUpdatedBy: entry.userEmail,
        });
      }
    }
    return result;
  }, [auditEntries, secretDetails]);

  // ─── Render: email still resolving ───────────────────────────────────────
  if (!currentUserEmail) {
    return (
      <Box p={3} display="flex" alignItems="center" gap={1}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading…</Typography>
      </Box>
    );
  }

  // ─── Render: non-owner ────────────────────────────────────────────────────
  if (!isOwner) return null;

  // ─── Render helpers ──────────────────────────────────────────────────────
  const renderSecretsContent = () => {
    if (loadingKeys) {
      return (
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading secrets…</Typography>
        </Box>
      );
    }
    if (fetchError) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          {fetchError}
        </Alert>
      );
    }
    if (secretDetails.length === 0) {
      // All BYOG cases (including legacy annotation) are handled by isByogMode
      // above and never reach this branch. The only remaining empty case is a
      // genuine SkyStage Managed app with no secrets yet.
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          No secrets configured yet. Add secrets via the Deploy / Redeploy form.
        </Alert>
      );
    }
    return (
      <TableContainer sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Secret Key</strong>
              </TableCell>
              <TableCell>
                <strong>Mount Path</strong>
              </TableCell>
              <TableCell>
                <strong>Last Updated</strong>
              </TableCell>
              <TableCell>
                <strong>Updated By</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {secretDetails.map(detail => (
              <TableRow key={detail.key}>
                <TableCell>
                  <Chip label={detail.key} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                  >
                    /var/secrets/{detail.key}.txt
                  </Typography>
                </TableCell>
                <TableCell>
                  {detail.lastUpdated ? (
                    <Tooltip
                      title={new Date(detail.lastUpdated).toLocaleString()}
                      placement="top"
                    >
                      <Typography variant="body2" sx={{ cursor: 'default' }}>
                        {formatRelative(detail.lastUpdated)}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      —
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {detail.lastUpdatedBy ?? '—'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // IDP-983: Render dual-env secrets as two separate cards (Staging + Production)
  const renderDualEnvSecretsContent = () => {
    if (loadingKeys) {
      return (
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading secrets…</Typography>
        </Box>
      );
    }
    if (fetchError) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          {fetchError}
        </Alert>
      );
    }
    if (dualEnvSecrets.length === 0) {
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          No secrets configured yet. Add secrets via the Deploy / Redeploy form.
        </Alert>
      );
    }

    const stagingSecrets = dualEnvSecrets.filter(s => s.stagingExists);
    const productionSecrets = dualEnvSecrets.filter(s => s.productionExists);

    const renderEnvTable = (
      secrets: DualEnvSecret[],
      env: 'staging' | 'production',
    ) => {
      if (secrets.length === 0) {
        return (
          <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
            No secrets configured for this environment.
          </Typography>
        );
      }
      return (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Secret Key</strong>
                </TableCell>
                <TableCell>
                  <strong>Mount Path</strong>
                </TableCell>
                <TableCell>
                  <strong>Last Updated</strong>
                </TableCell>
                <TableCell>
                  <strong>Updated By</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {secrets.map(secret => {
                const lastUpdated =
                  env === 'staging'
                    ? secret.stagingLastUpdated
                    : secret.productionLastUpdated;
                const updatedBy =
                  env === 'staging'
                    ? secret.stagingUpdatedBy
                    : secret.productionUpdatedBy;
                return (
                  <TableRow key={secret.key}>
                    <TableCell>
                      <Chip
                        label={secret.key}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                      >
                        /var/secrets/{secret.key}.txt
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {lastUpdated ? (
                        <Tooltip
                          title={new Date(lastUpdated).toLocaleString()}
                          placement="top"
                        >
                          <Typography
                            variant="body2"
                            sx={{ cursor: 'default' }}
                          >
                            {formatRelative(lastUpdated)}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {updatedBy ?? '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      );
    };

    // IDP-1186: Show cards based on live environment deployment status
    // 1. Both staging and prod deployed → show both cards
    // 2. Only staging deployed → show only staging card
    // 3. Only prod deployed (staging torn down) → show only prod card
    const showStaging =
      stagingDeployed || (!productionDeployed && !stagingDeployed);
    const showProduction = productionDeployed;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Staging Secrets Card — IDP-1186: shown when staging is deployed */}
        {showStaging && (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                bgcolor: '#fff3e0', // Orange 50 - Staging warning color
                px: 2,
                py: 1,
                borderBottom: '1px solid',
                borderColor: '#ffcc80', // Orange 200
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: '#e65100' }}
              >
                Staging Environment
              </Typography>
            </Box>
            {renderEnvTable(stagingSecrets, 'staging')}
          </Box>
        )}

        {/* Production Secrets Card — IDP-1186: shown when production is deployed */}
        {showProduction && (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                bgcolor: '#e8f5e9', // Green 50 - Production success color
                px: 2,
                py: 1,
                borderBottom: '1px solid',
                borderColor: '#a5d6a7', // Green 200
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: '#2e7d32' }}
              >
                Production Environment
              </Typography>
            </Box>
            {renderEnvTable(productionSecrets, 'production')}
          </Box>
        )}

        {/* IDP-1186: Info message when production not yet available */}
        {!showProduction && showStaging && (
          <Alert severity="info">
            Production secrets will be available after you promote your app to
            production.
          </Alert>
        )}
      </Box>
    );
  };

  const renderAuditContent = () => {
    if (loadingAudit) {
      return (
        <Box p={2} display="flex" alignItems="center" gap={1}>
          <CircularProgress size={16} />
          <Typography variant="body2">Loading audit log…</Typography>
        </Box>
      );
    }
    if (auditEntries.length === 0) {
      return (
        <Box p={2}>
          <Typography variant="body2" color="textSecondary">
            No changes recorded in this server session.
          </Typography>
        </Box>
      );
    }
    const getActionColor = (
      action: string,
    ): 'error' | 'success' | 'default' => {
      if (action === 'deleted') return 'error';
      if (action === 'created') return 'success';
      return 'default';
    };
    const getEnvColor = (
      env: string | undefined,
    ): 'warning' | 'success' | 'default' => {
      if (env === 'staging') return 'warning';
      if (env === 'production') return 'success';
      return 'default';
    };
    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Action</strong>
              </TableCell>
              <TableCell>
                <strong>Key</strong>
              </TableCell>
              {stagingEnabled && (
                <TableCell>
                  <strong>Environment</strong>
                </TableCell>
              )}
              <TableCell>
                <strong>By</strong>
              </TableCell>
              <TableCell>
                <strong>When</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {auditEntries.map((rawEntry, idx) => {
              // IDP-983: Normalize legacy entries (strip STAGING_ prefix, infer environment)
              const entry = normalizeAuditEntry(rawEntry);
              return (
                <TableRow key={idx}>
                  <TableCell>
                    <Chip
                      label={entry.action}
                      size="small"
                      color={getActionColor(entry.action)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                    >
                      {entry.key}
                    </Typography>
                  </TableCell>
                  {stagingEnabled && (
                    <TableCell>
                      <Chip
                        label={entry.environment || 'production'}
                        size="small"
                        color={getEnvColor(entry.environment)}
                        variant="outlined"
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography variant="body2">{entry.userEmail}</Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip
                      title={new Date(entry.timestamp).toLocaleString()}
                      placement="top"
                    >
                      <Typography variant="body2" sx={{ cursor: 'default' }}>
                        {formatRelative(entry.timestamp)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // ─── Render: owner read-only view ─────────────────────────────────────────
  // BYOG mode detection — four complementary signals in priority order:
  //
  // 1. API-based (most authoritative): the backend returned byogSecrets.
  //    This is the normal case for modern apps with hasByogSecrets='true'.
  //
  // 2. Annotation-based (when API returned empty but annotation says BYOG):
  //    Handles BYOG apps that haven't configured any secret names yet.
  //
  // 3. Audit-derived (fallback when catalog is stale): the audit log reports
  //    active (non-deleted) keys that don't exist in GCP SM — BYOG secrets
  //    that haven't yet appeared in the catalog annotation.
  //
  // 4. Legacy annotation (pre-BYOG feature apps): hasGsmSecrets='true' with
  //    annotation keys but no live qp-prefixed secrets from the API. These apps
  //    were deployed before the hasByogSecrets annotation existed; their secrets
  //    live in the user's own GCP project and should show as BYOG until redeployed.
  const isByogFromApi =
    byogSecretDetails.length > 0 && secretDetails.length === 0;
  const isByogFromAnnotation =
    !isByogFromApi &&
    hasByogSecrets &&
    byogSecretNames.length > 0 &&
    secretDetails.length === 0 &&
    !fetchError;
  const isByogFromAudit =
    !isByogFromApi &&
    !hasByogSecrets &&
    auditDerivedByogDetails.length > 0 &&
    secretDetails.length === 0 &&
    !loadingAudit &&
    !fetchError;
  const isByogFromLegacyAnnotation =
    !isByogFromApi &&
    !isByogFromAnnotation &&
    !isByogFromAudit &&
    annotationGsmKeys.length > 0 &&
    secretDetails.length === 0 &&
    !fetchError;
  const isByogMode =
    isByogFromApi ||
    isByogFromAnnotation ||
    isByogFromAudit ||
    isByogFromLegacyAnnotation;

  // When annotation is stale, derive display values from the audit log.
  // byogGcpProjectId won't be available from audit data; show a placeholder.
  const isAnnotationStale = isByogFromAudit;
  // Legacy apps haven't been redeployed since BYOG annotations were introduced.
  const isLegacyAnnotation = isByogFromLegacyAnnotation;

  // Prefer API-fetched BYOG details (with metadata); fall back to annotation/audit names.
  let displayByogDetails: SecretDetail[];
  if (byogSecretDetails.length > 0) {
    displayByogDetails = byogSecretDetails;
  } else if (byogSecretNames.length > 0) {
    displayByogDetails = byogSecretNames.map(n => ({ key: n }));
  } else if (auditDerivedByogDetails.length > 0) {
    displayByogDetails = auditDerivedByogDetails;
  } else {
    displayByogDetails = annotationGsmKeys.map(n => ({ key: n }));
  }

  // Prefer API-returned project ID (freshest); fall back to catalog annotation.
  const displayByogGcpProjectId = apiByogGcpProjectId || byogGcpProjectId;

  const readOnlyAlert = (
    <Alert severity="info" sx={{ mb: 2 }}>
      This tab is <strong>read-only</strong>. To add, edit, or remove secrets,
      use the <strong>Deploy / Redeploy form</strong>.
    </Alert>
  );

  return (
    <Box p={3}>
      {isByogMode ? (
        /* ── BYOG mode ─────────────────────────────────────────────── */
        <>
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <Typography variant="h6">Application Secrets</Typography>
            <Chip
              label="BYOG (User Managed)"
              size="small"
              color="default"
              variant="outlined"
            />
          </Box>
          {displayByogGcpProjectId && (
            <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
              Your GCP Project: <code>{displayByogGcpProjectId}</code>
            </Typography>
          )}
          <Typography variant="body2" color="textSecondary" gutterBottom>
            These secrets live in your own GCP project. SkyStage has granted
            read access to your app's service account. Values are never
            displayed.
          </Typography>

          {readOnlyAlert}

          {/* Stale-annotation notice: shown when BYOG mode was inferred from
              the audit log because the catalog entity hasn't refreshed yet.
              Provides a Refresh button to re-index the entity from GitHub. */}
          {isAnnotationStale && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={
                isRefreshing ? (
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                ) : (
                  <Button
                    size="small"
                    color="inherit"
                    onClick={handleRefreshEntity}
                  >
                    Refresh
                  </Button>
                )
              }
            >
              Entity data is loading — secret names are inferred from the audit
              log. Click <strong>Refresh</strong> to reload the latest
              configuration (GCP Project ID will appear after refresh).
            </Alert>
          )}

          {/* Legacy annotation notice: shown for apps deployed before the BYOG
              feature existed. Secret names come from the old hasGsmSecrets/
              gsmSecretKeys annotations. Redeploying will migrate to the
              current format and surface live metadata. */}
          {isLegacyAnnotation && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Secret keys shown from catalog data. To see live metadata and
              migrate to the current format, use <strong>Redeploy</strong>.
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          {loadingKeys ? (
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading secrets…</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Secret Name</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Mount Path</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Last Updated</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Updated By</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayByogDetails.map(detail => (
                    <TableRow key={detail.key}>
                      <TableCell>
                        <Chip
                          label={detail.key}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                        >
                          /var/secrets/{detail.key}.txt
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {detail.lastUpdated ? (
                          <Tooltip
                            title={new Date(
                              detail.lastUpdated,
                            ).toLocaleString()}
                            placement="top"
                          >
                            <Typography
                              variant="body2"
                              sx={{ cursor: 'default' }}
                            >
                              {formatRelative(detail.lastUpdated)}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {detail.lastUpdatedBy ?? '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      ) : (
        /* ── SkyStage Managed mode ──────────────────────────────────── */
        <>
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <Typography variant="h6">Application Secrets</Typography>
            {/* Only badge as SkyStage Managed when live API data confirms it.
                When falling back to annotation keys (legacy apps not yet migrated),
                we cannot determine the management mode with certainty. */}
            {secretDetails.length > 0 && (
              <Chip
                label="SkyStage Managed"
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Secrets are stored in GCP Secret Manager and mounted at{' '}
            <code>/var/secrets/{'<KEY>'}.txt</code> inside your container.
            Values are never displayed.
          </Typography>

          {readOnlyAlert}

          <Divider sx={{ my: 2 }} />

          {/* IDP-983: Show dual-env view (Staging + Production cards) when staging is enabled */}
          {stagingEnabled && dualEnvSecrets.length > 0
            ? renderDualEnvSecretsContent()
            : renderSecretsContent()}
        </>
      )}

      <Divider sx={{ my: 2 }} />

      {/* ── Audit log — shown in both modes ── */}
      <Accordion
        disableGutters
        elevation={0}
        sx={{ border: '1px solid', borderColor: 'divider' }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">
            Audit Log{' '}
            {auditEntries.length > 0 && (
              <Chip label={auditEntries.length} size="small" sx={{ ml: 1 }} />
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {renderAuditContent()}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
